import React, { Component } from 'react';
import moment from 'moment-timezone';
import alertify from 'alertifyjs';
import SimpleReactValidator from 'simple-react-validator';
import isEqual from 'lodash/isEqual';
import cloneDeep from 'lodash/cloneDeep';
import service from '../../services';
import Loader from '../loader';
import AppRoutePicker from './AppRoutePicker';

const PERIODS = [
  { key: 'preFestival',    label: 'Pre-Festival' },
  { key: 'duringFestival', label: 'During Festival' },
  { key: 'postFestival',   label: 'Post-Festival' },
];

const PERIOD_OPTIONS = PERIODS.map(p => ({ value: p.key, label: p.label }));

const NAV_TYPE = { EXTERNAL_URL: 1, APP_PAGE: 2 };

const emptyPeriod = () => ({
  title:          '',
  body:           '',
  primaryCtaText: '',
  primaryCtaType: { type: NAV_TYPE.APP_PAGE, value: '' },
});

const emptyForm = () => ({
  preFestival:    emptyPeriod(),
  duringFestival: emptyPeriod(),
  postFestival:   emptyPeriod(),
});

class PersonalizedWelcomeMessage extends Component {
  constructor(props) {
    super(props);
    this.state = {
      activePeriod:  'preFestival',
      loaderFlag:    false,
      isDirty:       false,
      dataLoaded:    false,
      form:          emptyForm(),
      initialForm:   null,
      lastUpdatedAt: null,
      lastUpdatedBy: null,
    };
    this.validator = new SimpleReactValidator({ autoForceUpdate: this });
    this.apiserve  = new service();
  }

  componentDidMount() {
    this.setupSocketEvents();
    this.getData();
    this.setState({ loaderFlag: true });
  }

  componentWillUnmount() {
    if (this.sub) this.sub.unsubscribe();
  }

  setupSocketEvents() {
    this.sub = this.apiserve.getevent().subscribe((response) => {
      switch (response.event) {
        case 'homePageApp|personalizedWelcomeMessage|getList':
          if (!response.error) {
            const msg = response.data?.personalizedWelcomeMessage || {};
            const form = {
              preFestival:    this.mapPeriod(msg.preFestival),
              duringFestival: this.mapPeriod(msg.duringFestival),
              postFestival:   this.mapPeriod(msg.postFestival),
            };
            this.setState({
              form,
              initialForm:   cloneDeep(form),
              isDirty:       false,
              dataLoaded:    true,
              loaderFlag:    false,
              lastUpdatedAt: response.data?.updatedAt || null,
              lastUpdatedBy: response.data?.updatedBy || null,
            });
          } else {
            this.setState({ loaderFlag: false });
          }
          break;

        case 'homePageApp|personalizedWelcomeMessage|createUpdate':
          if (response.error) {
            this.setState({ loaderFlag: false });
            alertify.error(response.data?.error || 'Failed to save welcome message.');
          } else {
            this.setState({ loaderFlag: false });
            alertify.success('Welcome message saved successfully.');
            this.getData();
          }
          break;

        default:
          break;
      }
    });
  }

  getData() {
    this.apiserve.sendevent('homePageApp|personalizedWelcomeMessage|getList', {});
  }

  mapPeriod(period) {
    if (!period) return emptyPeriod();
    return {
      title:          period.title          || '',
      body:           period.body           || '',
      primaryCtaText: period.primaryCtaText || '',
      primaryCtaType: {
        type:  period.primaryCtaType?.type  || NAV_TYPE.APP_PAGE,
        value: period.primaryCtaType?.value || '',
      },
    };
  }

  handleFieldChange(periodKey, field, value) {
    this.setState(prev => {
      const form = { ...prev.form, [periodKey]: { ...prev.form[periodKey], [field]: value } };
      return { form, isDirty: !isEqual(form, prev.initialForm) };
    });
  }

  handleNavTypeChange(periodKey, type) {
    this.setState(prev => {
      const form = {
        ...prev.form,
        [periodKey]: { ...prev.form[periodKey], primaryCtaType: { type, value: '' } },
      };
      return { form, isDirty: !isEqual(form, prev.initialForm) };
    });
  }

  handleNavValueChange(periodKey, value) {
    this.setState(prev => {
      const form = {
        ...prev.form,
        [periodKey]: {
          ...prev.form[periodKey],
          primaryCtaType: { ...prev.form[periodKey].primaryCtaType, value },
        },
      };
      return { form, isDirty: !isEqual(form, prev.initialForm) };
    });
  }

  handleSave() {
    if (!this.validator.allValid()) {
      this.validator.showMessages();
      this.forceUpdate();
      return;
    }
    this.setState({ loaderFlag: true });
    this.apiserve.sendevent('homePageApp|personalizedWelcomeMessage|createUpdate', {
      personalizedWelcomeMessage: this.state.form,
    });
  }

  handleReset() {
    if (this.state.initialForm) {
      this.setState({ form: cloneDeep(this.state.initialForm), isDirty: false });
      this.validator.hideMessages();
    }
  }

  render() {
    const { activePeriod, form, isDirty, loaderFlag, lastUpdatedAt, lastUpdatedBy, dataLoaded } = this.state;
    const period     = form[activePeriod];
    const isExternal = period.primaryCtaType.type === NAV_TYPE.EXTERNAL_URL;

    return (
      <div className="sd_welcome_msg_page">

        {/* Period dropdown + last updated bar */}
        <div className="sd_app_config_wrapper sd_welcome_topbar">
          {lastUpdatedAt && (
            <p className="update_date">
              Last Update: {moment(lastUpdatedAt).tz('America/Denver').format('h:mm A')} MT &mdash; {moment(lastUpdatedAt).format('MM/DD/YY')}
              {lastUpdatedBy && ` Last Updated By: ${lastUpdatedBy.fname} ${lastUpdatedBy.lname}`}
            </p>
          )}
        </div>

        {/* Form card */}
        <div className="sd_app_config_wrapper">
          <div className="sd_welcome_form_card">

            <h3 className="sd_app_config_title">Personalized Welcome Message</h3>
            <div className="sd_app_config_hub_select sd_welcome_field_row">
            <label className="sd_welcome_label" htmlFor="periodSelect">Festival Period : </label>
            <div className="select_field sd_welcome_input_wrap">
              <select
                id="periodSelect"
                className="sd_select_input"
                value={activePeriod}
                onChange={e => this.setState({ activePeriod: e.target.value })}
              >
                {PERIOD_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          </div>
            {/* Header Title */}
            <div className="sd_welcome_field_row">
              <label className="sd_welcome_label">Header Title</label>
              <div className="sd_welcome_input_wrap">
                <input
                  type="text"
                  className="sd_welcome_input"
                  value={period.title}
                  onChange={e => this.handleFieldChange(activePeriod, 'title', e.target.value)}
                  placeholder='e.g. "Welcome {firstName}, we open soon"'
                  autoComplete="off"
                />
                <span className="sd_welcome_hint">Use &#123;firstName&#125; to insert the attendee's first name.</span>
                <span className="sd_welcome_error">
                  {this.validator.message(`title_${activePeriod}`, period.title, 'required')}
                </span>
              </div>
            </div>

            {/* Body Copy */}
            <div className="sd_welcome_field_row">
              <label className="sd_welcome_label">Body</label>
              <div className="sd_welcome_input_wrap">
                <textarea
                  className="sd_welcome_input sd_welcome_textarea"
                  value={period.body}
                  onChange={e => this.handleFieldChange(activePeriod, 'body', e.target.value)}
                  placeholder="Enter body copy (supports multiple lines)"
                  rows={4}
                />
                <span className="sd_welcome_error">
                  {this.validator.message(`body_${activePeriod}`, period.body, 'required')}
                </span>
              </div>
            </div>

            {/* CTA Text */}
            <div className="sd_welcome_field_row">
              <label className="sd_welcome_label">CTA Text</label>
              <div className="sd_welcome_input_wrap">
                <input
                  type="text"
                  className="sd_welcome_input"
                  value={period.primaryCtaText}
                  onChange={e => this.handleFieldChange(activePeriod, 'primaryCtaText', e.target.value)}
                  placeholder="e.g. Browse the lineup"
                  autoComplete="off"
                />
                <span className="sd_welcome_error">
                  {this.validator.message(`ctaText_${activePeriod}`, period.primaryCtaText, 'required')}
                </span>
              </div>
            </div>

            {/* CTA Type */}
            <div className="sd_welcome_field_row">
              <label className="sd_welcome_label p_0">CTA Type</label>
              <div className="sd_welcome_input_wrap">
                <div className="sd_app_config_radio_group">
                  <label className="sd_radio_label">
                    <input
                      type="radio"
                      name={`ctaType_${activePeriod}`}
                      checked={!isExternal}
                      onChange={() => this.handleNavTypeChange(activePeriod, NAV_TYPE.APP_PAGE)}
                    />
                    <span className="sd_radio_dot" />
                    App Page
                  </label>
                  <label className="sd_radio_label">
                    <input
                      type="radio"
                      name={`ctaType_${activePeriod}`}
                      checked={isExternal}
                      onChange={() => this.handleNavTypeChange(activePeriod, NAV_TYPE.EXTERNAL_URL)}
                    />
                    <span className="sd_radio_dot" />
                    External URL
                  </label>
                </div>
              </div>
            </div>

            {/* CTA Link */}
            <div className="sd_welcome_field_row">
              <label className="sd_welcome_label">CTA Link</label>
              <div className="sd_welcome_input_wrap">
                {isExternal ? (
                  <input
                    type="text"
                    className="sd_welcome_input"
                    value={period.primaryCtaType.value}
                    onChange={e => this.handleNavValueChange(activePeriod, e.target.value)}
                    placeholder="https://..."
                    autoComplete="off"
                  />
                ) : (
                  <AppRoutePicker
                    value={period.primaryCtaType.value}
                    onChange={val => this.handleNavValueChange(activePeriod, val)}
                    inputClass="sd_welcome_input"
                    error={this.validator.message(`ctaLink_${activePeriod}`, period.primaryCtaType.value, 'required')}
                  />
                )}
                {isExternal && (
                  <span className="sd_welcome_error">
                    {this.validator.message(`ctaLink_${activePeriod}`, period.primaryCtaType.value, 'required')}
                  </span>
                )}
              </div>
            </div>

          </div>
        </div>

        {/* Save / Reset */}
        <div className="text_center field_row w_100 buttons_outer">
          <button
            className="grayBTN uppercase"
            onClick={() => this.handleReset()}
            disabled={!dataLoaded}
          >
            Reset
          </button>
          <button
            className={`${isDirty ? 'active' : ''} uppercase`}
            onClick={() => this.handleSave()}
            disabled={!isDirty}
          >
            Save
          </button>
        </div>

        {loaderFlag && <Loader />}
      </div>
    );
  }
}

export default PersonalizedWelcomeMessage;